import { m, type Message } from '@/i18n';
import type { Actor } from '../seats';
import { decide, stateAfter } from '../state-machine';
import { domainError, type Command, type DomainError, type WorkItemState } from '../types';
import { planId } from './stimulus';
import type { Context, Intent, Plan, PlanStep, Question, Resolution, Stimulus } from './types';

/**
 * **Step 6: resolution** (docs/RESOLUTION.md §1). The compiled commands folded
 * step by step through `decide` against the state their predecessors leave -
 * the cascade deepest first, then the target's own - so a plan is legal iff
 * every step of it is. It also carries what the plan's consequences say, and
 * replays the fold for the optimistic end state a surface shows at the tap
 * (§10: the strike waits on nothing).
 */

/**
 * The fold (§1 step 6): run each command through `decide` against the state
 * its predecessors leave. A benign `no_change` is SKIPPED - the act compilers
 * already emit minimal sequences and a cascade may cover an item that is
 * already where it is going. Any other refusal refuses the whole plan, with
 * that step's typed error. An empty result (every command a no-op) is itself a
 * refusal, carrying the first no-op's message, so "already done" reads as it
 * should.
 */
function foldCommands(
  itemId: string,
  start: WorkItemState,
  actor: Actor,
  commands: readonly Command[],
): { ok: true; steps: PlanStep[]; state: WorkItemState } | { ok: false; error: DomainError } {
  let state = start;
  const steps: PlanStep[] = [];
  let firstNoop: DomainError | null = null;
  for (const command of commands) {
    const decided = decide(state, command, actor);
    if (!decided.ok) {
      if (decided.error.code === 'no_change') {
        firstNoop ??= decided.error;
        continue;
      }
      return { ok: false, error: decided.error };
    }
    for (const event of decided.value) {
      steps.push({ itemId, command, ...(event.level ? { level: event.level } : {}) });
      state = stateAfter(state, event);
    }
  }
  if (steps.length === 0) {
    return {
      ok: false,
      error: firstNoop ?? domainError('no_change', m('resolution.refusal.nothingToDo')),
    };
  }
  return { ok: true, steps, state };
}

/**
 * **The state a plan leaves on ONE item** - the optimistic reading every
 * surface shows at the tap (§10: "the strike waits on nothing"). The steps were
 * already folded when the plan was made; this replays that fold so the surface
 * holds the end state rather than a second, hand-written expectation. A plan
 * spanning a cascade is filtered to the item asked about.
 */
export function foldPlanState(
  itemId: string,
  state: WorkItemState,
  actor: Actor,
  steps: readonly PlanStep[],
): WorkItemState {
  const commands = steps.filter((s) => s.itemId === itemId).map((s) => s.command);
  const folded = foldCommands(itemId, state, actor, commands);
  return folded.ok ? folded.state : state;
}

interface PlanOpts {
  consequences?: Message[];
  confirm?: Question;
  allowEmptySteps?: boolean;
}

/** Compile an intent into a plan: fold the cascade (deepest first), then the
 *  target's own commands, each step through `decide` against the evolving
 *  state. */
export function planFrom(
  stimulus: Stimulus,
  context: Context,
  intent: Intent,
  opts: PlanOpts = {},
): Resolution {
  const steps: PlanStep[] = [];
  for (const entry of intent.cascade ?? []) {
    // The cascade's items come from one of two gathers: the open subtree a
    // conclusion sweeps (§6), or the items a recovered origin plan touched,
    // which an un-tick reverses (§8). Either way the state is the caller's,
    // never re-read here.
    const state =
      context.children?.find((c) => c.itemId === entry.itemId)?.state ??
      context.originItems?.find((i) => i.itemId === entry.itemId)?.state;
    if (!state) continue;
    const folded = foldCommands(entry.itemId, state, context.actor, entry.commands);
    if (!folded.ok) {
      // A no-op child (already concluded under a race) is skipped; anything
      // else refuses the whole gesture - one statement, one transaction (§7).
      if (folded.error.code !== 'no_change') {
        return { kind: 'refused', intent, error: folded.error };
      }
      continue;
    }
    steps.push(...folded.steps);
  }

  if (intent.commands.length > 0) {
    const folded = foldCommands(intent.itemId, context.item, context.actor, intent.commands);
    if (!folded.ok) {
      const cascaded = steps.length > 0;
      if (!cascaded || folded.error.code !== 'no_change') {
        return { kind: 'refused', intent, error: folded.error };
      }
    } else {
      steps.push(...folded.steps);
    }
  }

  if (steps.length === 0 && !opts.allowEmptySteps) {
    return {
      kind: 'refused',
      intent,
      error: domainError('no_change', m('resolution.refusal.nothingToDo')),
    };
  }

  const consequences = [...(opts.consequences ?? [])];
  if (context.weight.openBlockers > 0) {
    consequences.push(
      m('resolution.consequence.dependenciesOpen', { count: context.weight.openBlockers }),
    );
  }

  const body = {
    steps,
    ...(intent.transition ? { transition: intent.transition } : {}),
    consequences,
  };
  const plan: Plan = {
    id: planId(stimulus.key, body),
    statement: intent.statement,
    intent,
    ...body,
    ...(intent.waits ? { waits: intent.waits } : {}),
    ...(opts.confirm ? { confirm: opts.confirm } : {}),
  };
  return { kind: 'plan', plan };
}
